import { useState } from 'react'
import type { Channel } from '@rp/contracts'
import { isApiError } from '../../api/client'
import { Card, Spinner } from '../../design'
import { ChannelList } from './ChannelList'
import { useChannels, useDisconnectChannel, useStartConnect } from './channelQueries'
import { ConnectChannel } from './ConnectChannel'
import { SETTINGS_COPY } from './copy'
import { DisconnectChannelDialog } from './DisconnectChannelDialog'
import { useMetaApp } from './metaAppQueries'

export const ChannelsSection = () => {
  const channels = useChannels()
  const metaApp = useMetaApp()
  const connect = useStartConnect()
  const disconnect = useDisconnectChannel()
  const [confirming, setConfirming] = useState<Channel | null>(null)
  const connecting = connect.isPending ? connect.variables : null
  const error = [connect.error, disconnect.error].find(isApiError)?.message ?? null

  const startConnect = (platform: Channel['platform']) => {
    disconnect.reset()
    connect.mutate(platform)
  }

  const askDisconnect = (channel: Channel) => {
    connect.reset()
    disconnect.reset()
    setConfirming(channel)
  }

  return (
    <Card as="section" className="settings-card">
      <h2>{SETTINGS_COPY.channels}</h2>
      {error === null ? null : (
        <p className="settings-error text-tone tone-danger" role="alert">
          {error}
        </p>
      )}
      {disconnect.isSuccess ? (
        <p className="settings-saved text-tone tone-success" role="status">
          {SETTINGS_COPY.disconnectDone(disconnect.variables.name)}
        </p>
      ) : null}
      {channels.data === undefined ? (
        <Spinner label={SETTINGS_COPY.loadingChannels} />
      ) : (
        <ChannelList channels={channels.data} connecting={connecting} onReconnect={startConnect} onDisconnect={askDisconnect} />
      )}
      <ConnectChannel
        connecting={connecting}
        disabled={metaApp.data === null || metaApp.data === undefined}
        onConnect={startConnect}
      />
      <DisconnectChannelDialog
        channel={confirming}
        pending={disconnect.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={(channel) => disconnect.mutate(channel, { onSettled: () => setConfirming(null) })}
      />
    </Card>
  )
}
